// Admin — 優惠碼（兌換碼）管理
// 列表 + 狀態/關鍵字篩選 + 建立/編輯/停用/刪除 + 使用明細（誰用了）
import { useState, useEffect, useCallback } from "react";
import Card from "@mui/material/Card";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, Divider, Box, Stack,
  FormControlLabel, Switch,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "AuthContext";
import { COLORS } from "theme/tokens";

const PAGE_SIZE = 20;

const apiCall = (url, method, body, token) =>
  fetch(url, {
    method: method || "GET",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  }).then((r) => r.json());

const STATE_META = {
  ACTIVE: { label: "啟用中", color: "success" },
  INACTIVE: { label: "已停用", color: "default" },
  SCHEDULED: { label: "未開始", color: "info" },
  EXPIRED: { label: "已過期", color: "warning" },
  EXHAUSTED: { label: "已用完", color: "error" },
};

const STATUS_FILTERS = [
  { value: "ALL", label: "全部" },
  { value: "ACTIVE", label: "啟用中" },
  { value: "INACTIVE", label: "已停用" },
];

const ELIGIBILITY_OPTS = [
  { value: "UNPAID", label: "尚未付費者（含過期/FREE）" },
  { value: "TRIAL_ACTIVE", label: "限體驗中" },
  { value: "ALL", label: "所有人" },
];
const ELIGIBILITY_LABEL = { UNPAID: "尚未付費者", TRIAL_ACTIVE: "限體驗中", ALL: "所有人" };

const fmt = (v) => (v ? new Date(v).toLocaleString() : "-");
const fmtRange = (a, b) => {
  if (!a && !b) return "永久";
  const s = a ? new Date(a).toLocaleDateString() : "即刻";
  const e = b ? new Date(b).toLocaleDateString() : "永久";
  return `${s} ~ ${e}`;
};
// ISO → datetime-local 值（YYYY-MM-DDTHH:mm，本地時區）
const toLocalInput = (v) => {
  if (!v) return "";
  const d = new Date(v);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const emptyForm = {
  id: null,
  code: "",
  trialExtendDays: 30,
  coinAmount: "",
  eligibility: "UNPAID",
  maxRedemptions: "",
  startsAt: "",
  endsAt: "",
  isActive: true,
  campaign: "",
};

function PromoCodesAdmin() {
  const { token } = useAuth();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [keyword, setKeyword] = useState("");
  const [keywordInput, setKeywordInput] = useState("");

  const [form, setForm] = useState(null); // 建立/編輯表單（null = 關閉）
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState(null); // 使用明細 dialog

  // ── fetch ──
  const fetchList = useCallback(() => {
    const qs = new URLSearchParams({ page: String(page + 1), pageSize: String(PAGE_SIZE) });
    if (statusFilter && statusFilter !== "ALL") qs.set("status", statusFilter);
    if (keyword) qs.set("keyword", keyword);
    apiCall(`/api/admin/promo-codes?${qs.toString()}`, "GET", null, token)
      .then((d) => {
        setRows(Array.isArray(d?.records) ? d.records : []);
        setTotal(d?.total || 0);
      })
      .catch(() => {
        setRows([]);
        setTotal(0);
      });
  }, [page, statusFilter, keyword, token]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // ── create / edit ──
  const openCreate = () => setForm({ ...emptyForm });
  const openEdit = (r) =>
    setForm({
      id: r.id,
      code: r.code,
      trialExtendDays: r.trialExtendDays || "",
      coinAmount: r.coinAmount || "",
      eligibility: r.eligibility,
      maxRedemptions: r.maxRedemptions ?? "",
      startsAt: toLocalInput(r.startsAt),
      endsAt: toLocalInput(r.endsAt),
      isActive: r.isActive,
      campaign: r.campaign || "",
    });
  const closeForm = () => setForm(null);

  const submitForm = async () => {
    if (!form) return;
    if (!form.id && !form.code.trim()) { window.alert("請輸入優惠碼"); return; }
    const tDays = form.trialExtendDays === "" ? 0 : Number(form.trialExtendDays);
    const cAmt = form.coinAmount === "" ? 0 : Number(form.coinAmount);
    if (!(tDays > 0) && !(cAmt > 0)) { window.alert("請至少設定一種優惠（延長體驗天數 或 MAS Coin）"); return; }
    setSaving(true);
    try {
      const body = {
        trialExtendDays: tDays > 0 ? tDays : null,
        coinAmount: cAmt > 0 ? cAmt : null,
        eligibility: form.eligibility,
        maxRedemptions: form.maxRedemptions === "" ? null : Number(form.maxRedemptions),
        startsAt: form.startsAt || null,
        endsAt: form.endsAt || null,
        isActive: form.isActive,
        campaign: form.campaign.trim() || null,
      };
      let res;
      if (form.id) {
        res = await apiCall("/api/admin/promo-codes/update", "POST", { id: form.id, ...body }, token);
      } else {
        res = await apiCall("/api/admin/promo-codes/create", "POST", { code: form.code.trim(), ...body }, token);
      }
      if (res?.id) {
        closeForm();
        fetchList();
      } else {
        window.alert(res?.message || "儲存失敗");
      }
    } catch (e) {
      window.alert("儲存失敗");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (r) => {
    const res = await apiCall("/api/admin/promo-codes/update", "POST", { id: r.id, isActive: !r.isActive }, token);
    if (res?.id) fetchList();
    else window.alert(res?.message || "操作失敗");
  };

  const removeCode = async (r) => {
    if (!window.confirm(`確定刪除優惠碼「${r.code}」？此動作無法復原。`)) return;
    const res = await apiCall("/api/admin/promo-codes/delete", "POST", { id: r.id }, token);
    if (res?.id) fetchList();
    else window.alert(res?.message || "刪除失敗");
  };

  const openUsage = async (r) => {
    const d = await apiCall(`/api/admin/promo-codes/${r.id}/usage`, "GET", null, token);
    setUsage(d || { code: r, records: [], total: 0 });
  };

  const exportUsageCsv = async () => {
    if (!usage?.code?.id) return;
    const res = await fetch(`/api/admin/promo-codes/${usage.code.id}/export`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `promo_${usage.code.code}_redemptions.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <MDTypography variant="h6" color="white">優惠碼管理</MDTypography>
            <MDBox display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
              <TextField
                select
                size="small"
                label="狀態"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                sx={{
                  minWidth: 130,
                  borderRadius: 1,
                  "& .MuiInputBase-input": { color: COLORS.white },
                  "& .MuiSelect-icon": { color: COLORS.white },
                  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.9)" },
                  "& fieldset": { borderColor: "rgba(255,255,255,0.7)" },
                  "&:hover fieldset": { borderColor: "#fff !important" },
                }}
                InputLabelProps={{ shrink: true }}
              >
                {STATUS_FILTERS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </TextField>
              <MDInput
                placeholder="搜尋優惠碼"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { setKeyword(keywordInput); setPage(0); } }}
                size="small"
                sx={{
                  minWidth: 200,
                  borderRadius: 1,
                  "& .MuiInputBase-input": { color: COLORS.white },
                  "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.85)", opacity: 1 },
                  "& fieldset": { borderColor: "rgba(255,255,255,0.7)" },
                  "&:hover fieldset": { borderColor: "#fff !important" },
                }}
              />
              <MDButton
                variant="contained"
                color="white"
                size="small"
                onClick={() => { setKeyword(keywordInput); setPage(0); }}
                sx={{ color: "#F36D26 !important", fontWeight: 700, px: 2.5, boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
              >
                篩選
              </MDButton>
              <MDButton variant="contained" color="success" size="small" onClick={openCreate} sx={{ fontWeight: 700, px: 2.5 }}>
                ＋ 新增優惠碼
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox pt={3} px={2} pb={2}>
            <TableContainer sx={{ boxShadow: "none" }}>
              <Table>
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>優惠碼</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>優惠</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>資格</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>有效期限</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">已用 / 上限</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>狀態</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5, color: "text.secondary" }}>
                        目前尚無優惠碼
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => {
                      const meta = STATE_META[r.state] || STATE_META.ACTIVE;
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell>
                            <MDTypography variant="button" fontWeight="bold">{r.code}</MDTypography>
                            {r.campaign && (
                              <MDTypography variant="caption" color="text" display="block">{r.campaign}</MDTypography>
                            )}
                          </TableCell>
                          <TableCell>
                            {r.trialExtendDays > 0 && <div>體驗 +{r.trialExtendDays} 天</div>}
                            {r.coinAmount > 0 && <div>+{r.coinAmount} Coin</div>}
                          </TableCell>
                          <TableCell>{ELIGIBILITY_LABEL[r.eligibility] || r.eligibility}</TableCell>
                          <TableCell>{fmtRange(r.startsAt, r.endsAt)}</TableCell>
                          <TableCell align="center">
                            {r.redeemedCount} / {r.maxRedemptions == null ? "∞" : r.maxRedemptions}
                          </TableCell>
                          <TableCell>
                            <Chip label={meta.label} color={meta.color} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                              <MDButton size="small" variant="text" color="info" onClick={() => openUsage(r)}>使用</MDButton>
                              <MDButton size="small" variant="text" color="dark" onClick={() => openEdit(r)}>編輯</MDButton>
                              <MDButton size="small" variant="text" color={r.isActive ? "warning" : "success"} onClick={() => toggleActive(r)}>
                                {r.isActive ? "停用" : "啟用"}
                              </MDButton>
                              {r.redeemedCount === 0 && (
                                <MDButton size="small" variant="text" color="error" onClick={() => removeCode(r)}>刪除</MDButton>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* pager */}
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={2}>
              <MDTypography variant="caption" color="text">共 {total} 筆</MDTypography>
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton size="small" disabled={page <= 0} onClick={() => setPage((p) => p - 1)}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <MDTypography variant="caption">{page + 1} / {totalPages}</MDTypography>
                <IconButton size="small" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Box>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>

      {/* create / edit dialog */}
      <Dialog open={!!form} onClose={closeForm} maxWidth="sm" fullWidth>
        <DialogTitle>{form?.id ? "編輯優惠碼" : "新增優惠碼"}</DialogTitle>
        <Divider sx={{ m: 0 }} />
        <DialogContent>
          {form && (
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <TextField
                label="優惠碼"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                size="small"
                fullWidth
                disabled={!!form.id}
                helperText={form.id ? "建立後不可更改碼字串" : "限大寫英數、_、-，2~40 字"}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="延長體驗天數（留空 = 不延長）"
                  type="number"
                  value={form.trialExtendDays}
                  onChange={(e) => setForm((f) => ({ ...f, trialExtendDays: e.target.value }))}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="贈送 MAS Coin（留空 = 不送）"
                  type="number"
                  value={form.coinAmount}
                  onChange={(e) => setForm((f) => ({ ...f, coinAmount: e.target.value }))}
                  size="small"
                  fullWidth
                />
              </Stack>
              <TextField
                select
                label="使用資格"
                value={form.eligibility}
                onChange={(e) => setForm((f) => ({ ...f, eligibility: e.target.value }))}
                size="small"
                fullWidth
              >
                {ELIGIBILITY_OPTS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="總使用上限（留空 = 不限）"
                type="number"
                value={form.maxRedemptions}
                onChange={(e) => setForm((f) => ({ ...f, maxRedemptions: e.target.value }))}
                size="small"
                fullWidth
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="有效起（留空 = 即刻）"
                  type="datetime-local"
                  value={form.startsAt}
                  onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="有效訖（留空 = 永久）"
                  type="datetime-local"
                  value={form.endsAt}
                  onChange={(e) => setForm((f) => ({ ...f, endsAt: e.target.value }))}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
              <TextField
                label="活動標籤 / 備註（選填）"
                value={form.campaign}
                onChange={(e) => setForm((f) => ({ ...f, campaign: e.target.value }))}
                size="small"
                fullWidth
              />
              <FormControlLabel
                control={<Switch checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />}
                label="啟用"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton onClick={closeForm} color="secondary">取消</MDButton>
          <MDButton onClick={submitForm} color="info" variant="gradient" disabled={saving}>
            {saving ? "儲存中..." : "儲存"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* usage dialog */}
      <Dialog open={!!usage} onClose={() => setUsage(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          兌換名單{usage?.code?.code ? `：${usage.code.code}` : ""}
          {usage?.total != null && (
            <MDTypography variant="caption" color="text" sx={{ ml: 1 }}>（共 {usage.total} 人）</MDTypography>
          )}
        </DialogTitle>
        <Divider sx={{ m: 0 }} />
        <DialogContent>
          {(!usage?.records || usage.records.length === 0) ? (
            <MDBox py={4} textAlign="center">
              <MDTypography variant="button" color="text">尚無人兌換</MDTypography>
            </MDBox>
          ) : (
            <TableContainer sx={{ boxShadow: "none" }}>
              <Table size="small">
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>使用者</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">獲得</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>兌換時間</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usage.records.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>
                        <MDTypography variant="button" fontWeight="medium" display="block">{r.user?.fullName || "-"}</MDTypography>
                        <MDTypography variant="caption" color="text">{r.user?.email || "-"}</MDTypography>
                      </TableCell>
                      <TableCell align="center">
                        {[r.trialExtendDays > 0 ? `${r.trialExtendDays}天` : null, r.coinAmount > 0 ? `${r.coinAmount} Coin` : null].filter(Boolean).join(" + ") || "-"}
                      </TableCell>
                      <TableCell>{fmt(r.redeemedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {usage?.records?.length > 0 && (
            <MDButton onClick={exportUsageCsv} color="info" variant="outlined">匯出 CSV</MDButton>
          )}
          <MDButton onClick={() => setUsage(null)} color="secondary">關閉</MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default PromoCodesAdmin;
